<script>
  import { createEventDispatcher } from "svelte";
  import InsetMap from "./InsetMap.svelte";

  export let bounds;
  export let stateBoundaries;
  export let riverPath;
  export let currentLocation;
  export let vizState;
  export let activeFeatureIndex;
  export let startCoordinates;
  export let featureGroups;
  export let suggestionModalActive;

  // River info props (passed from parent)
  export let riverDisplayName = "River";
  export let riverDestination = "";
  export let stoppingFeatureName = "";

  // Playback controls props
  export let paused;
  export let togglePause;
  export let playbackSpeed;
  export let setPlaybackSpeed;
  export let altitudeMultiplier;
  export let setAltitudeMultipier;

  const dispatch = createEventDispatcher();

  // Only show during running state
  $: isVisible = vizState === "running" && activeFeatureIndex >= 0;

  // Format destination for display
  $: destinationText = stoppingFeatureName || riverDestination || "the sea";

  const handleExit = () => {
    dispatch("abort-run");
  };

  const handleHideSuggestionModal = () => {
    dispatch("hide-suggestion-modal");
  };
</script>

<div
  class="flight-deck"
  class:visible={isVisible}
  aria-label="Flight controls"
>
  <!-- Close Button (Top Right Corner) -->
  <button class="close-btn" on:click={handleExit} title="Exit fly mode">
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>
  </button>

  <!-- Left Side: Inset Map (The Radar) -->
  <div class="radar-section">
    <InsetMap
      {bounds}
      {stateBoundaries}
      visibleIndex={isVisible ? 1 : null}
      {riverPath}
      {currentLocation}
      {vizState}
      {activeFeatureIndex}
      {startCoordinates}
      {featureGroups}
      {suggestionModalActive}
      mapStyle="mapbox://styles/mapbox/dark-v11"
      on:hide-suggestion-modal={handleHideSuggestionModal}
    />
  </div>

  <!-- Right Side: Info + Controls -->
  <div class="info-section">
    <!-- River Info Header -->
    <div class="river-header">
      <div class="river-name">{riverDisplayName}</div>
      <div class="river-destination">Flowing to {destinationText}</div>
    </div>

    <!-- Playback Controls -->
    <div class="playback-row">
      <!-- Speed Slider with Labels -->
      <div class="speed-control">
        <span class="speed-label">CLOSE</span>
        <div class="scrubber">
          <input
            type="range"
            id="flight-scrubber"
            name="altitude"
            min="0.6"
            max="5"
            step="0.01"
            value={altitudeMultiplier}
            on:input={(e) => setAltitudeMultipier(e)}
          />
        </div>
        <span class="speed-label">WIDE</span>
      </div>

      <!-- Play/Pause Button -->
      <button
        class="play-btn"
        on:click={togglePause}
        title={paused || playbackSpeed !== 1 ? "Play" : "Pause"}
      >
        {#if paused || playbackSpeed !== 1}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 2L14 8L4 14V2Z"/>
          </svg>
        {:else}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="3" y="2" width="4" height="12"/>
            <rect x="9" y="2" width="4" height="12"/>
          </svg>
        {/if}
      </button>

      <!-- Speed buttons (subtle) -->
      <div class="speed-btns">
        <button
          class="speed-btn"
          class:active={playbackSpeed === -1}
          on:click={() => setPlaybackSpeed(-1)}
          title="Rewind"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 1L1 6L6 11V1Z"/>
            <path d="M12 1L7 6L12 11V1Z"/>
          </svg>
        </button>
        <button
          class="speed-btn"
          class:active={playbackSpeed === 2}
          on:click={() => setPlaybackSpeed(2)}
          title="Fast forward"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M0 1L5 6L0 11V1Z"/>
            <path d="M6 1L11 6L6 11V1Z"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</div>

<style type="text/scss">
  @import "../settings.scss";

  .flight-deck {
    position: fixed;
    bottom: 32px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 35;

    display: flex;
    align-items: stretch;
    gap: 1rem;

    // Dark Glass aesthetic
    background: rgba(12, 12, 14, 0.88);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 0.875rem 1rem;
    padding-right: 2.5rem; // Space for close button

    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);

    // Hidden by default
    opacity: 0;
    pointer-events: none;
    transform: translateX(-50%) translateY(20px);
    transition: opacity 0.4s ease, transform 0.4s ease;

    &.visible {
      opacity: 1;
      pointer-events: auto;
      transform: translateX(-50%) translateY(0);
    }
  }

  // Close Button (Top Right)
  .close-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      color: rgba(255, 255, 255, 0.8);
    }
  }

  // Radar Section (Inset Map) - 50% larger than original
  .radar-section {
    position: relative;
    width: 210px;
    height: 140px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.4);
    flex-shrink: 0;

    // Override InsetMap styles for embedded display
    :global(.map) {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      border-radius: 12px !important;
      opacity: 1 !important;
      z-index: 1 !important;
    }

    :global(.marker) {
      z-index: 2 !important;
      opacity: 1 !important;
    }
  }

  // Info Section
  .info-section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 220px;
    gap: 0.5rem;
  }

  // River Header
  .river-header {
    .river-name {
      font-size: 1.1rem;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.95);
      line-height: 1.2;
      letter-spacing: 0.02em;
    }

    .river-destination {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.45);
      margin-top: 0.15rem;
    }
  }

  // Playback Row
  .playback-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  // Speed Control (wrapper for slider + labels)
  .speed-control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .speed-label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.35);
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  // Scrubber
  .scrubber {
    flex: 1;

    input[type="range"] {
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 3px;
      background: rgba(255, 255, 255, 0.12);
      border-radius: 2px;
      outline: none;
      cursor: pointer;

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #38B6FF;
        cursor: pointer;
        border: none;
        box-shadow: 0 0 8px rgba(56, 182, 255, 0.5);
        transition: transform 0.15s ease;

        &:hover {
          transform: scale(1.2);
        }
      }

      &::-moz-range-thumb {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #38B6FF;
        cursor: pointer;
        border: none;
        box-shadow: 0 0 8px rgba(56, 182, 255, 0.5);
      }

      // Track fill (webkit only)
      &::-webkit-slider-runnable-track {
        background: linear-gradient(to right,
          rgba(56, 182, 255, 0.4) 0%,
          rgba(255, 255, 255, 0.12) 100%
        );
        border-radius: 2px;
      }
    }
  }

  // Play Button
  .play-btn {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    border: none;
    background: #38B6FF;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: #5cc4ff;
      transform: scale(1.05);
    }

    svg {
      margin-left: 2px; // Visual centering for play icon
    }
  }

  // Speed Buttons
  .speed-btns {
    display: flex;
    gap: 0.25rem;
  }

  .speed-btn {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 4px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.35);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;

    &:hover {
      color: rgba(255, 255, 255, 0.7);
      background: rgba(255, 255, 255, 0.08);
    }

    &.active {
      color: #38B6FF;
      background: rgba(56, 182, 255, 0.15);
    }
  }

  // Mobile Styles
  @media only screen and (max-width: $mobile-breakpoint) {
    .flight-deck {
      bottom: 16px;
      left: 1rem;
      right: 1rem;
      transform: none;
      padding: 0.75rem;
      padding-right: 2rem;
      border-radius: 14px;
      gap: 0.75rem;

      &.visible {
        transform: none;
      }
    }

    .radar-section {
      width: 120px;
      height: 80px;
      border-radius: 8px;
    }

    .info-section {
      min-width: 130px;
      gap: 0.35rem;
    }

    .river-header {
      .river-name {
        font-size: 0.9rem;
      }

      .river-destination {
        font-size: 0.6rem;
      }
    }

    .playback-row {
      gap: 0.4rem;
    }

    .speed-control {
      gap: 0.25rem;
    }

    .speed-label {
      display: none; // Hide labels on mobile to save space
    }

    .play-btn {
      width: 1.75rem;
      height: 1.75rem;

      svg {
        width: 10px;
        height: 10px;
      }
    }

    .speed-btns {
      display: none;
    }

    .close-btn {
      top: 0.4rem;
      right: 0.4rem;
      width: 1.25rem;
      height: 1.25rem;

      svg {
        width: 10px;
        height: 10px;
      }
    }
  }

  // Tablet
  @media only screen and (min-width: 701px) and (max-width: 1100px) {
    .flight-deck {
      bottom: 24px;
    }

    .radar-section {
      width: 170px;
      height: 115px;
    }

    .info-section {
      min-width: 180px;
    }

    .river-header {
      .river-name {
        font-size: 1rem;
      }
    }
  }
</style>
